import { useEffect, useState } from "react"
import { Badge, Card, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"

interface Vehiculo {
    idInterno: string
    vin: string
    estado: string
    estadoTransferencia: string
    tipoVehiculo: string
    marca: string
    modelo: string
    anio: number | null
    color: string
    matricula: string
    nombrePropietario: string
    puertoEntrada: string
    fechaRegistroPlaca: string
    cedulaPropietario: string | null
}

const RecordVehiculos = (props: { id?: string; name?: string; matricula?: string }) => {
    const { id, name, matricula } = props
    const { t } = useTranslation()
    const [records, setRecords] = useState<Vehiculo[]>([])

    const bindRecords = async () => {
        try {
            const map = new Map<string, Vehiculo>()

            // 1. If matricula/VIN is provided directly, fetch it first
            if (matricula && matricula.trim()) {
                const cleanMat = matricula.trim()
                const res = await API.postAction("Vehiculos/List", {
                    matricula: cleanMat,
                    page: 0,
                    quantity: 20,
                })
                if (res.data.status === "ok" && res.data.response?.length > 0) {
                    res.data.response.forEach((v: Vehiculo) => {
                        const key = v.vin || v.matricula || v.idInterno
                        if (key) map.set(key, v)
                    })
                }
            }

            // 2. Search by cedula (exact, uses index)
            if (id) {
                const res = await API.postAction("Vehiculos/List", {
                    cedula: id,
                    page: 0,
                    quantity: 20,
                })
                if (res.data.status === "ok" && res.data.response?.length > 0) {
                    res.data.response.forEach((v: Vehiculo) => {
                        const key = v.vin || v.matricula || v.idInterno
                        if (key) map.set(key, v)
                    })
                }
            }

            // 3. Fallback: search by name if map is empty
            if (map.size === 0 && name && name.trim()) {
                const res = await API.postAction("Vehiculos/List", {
                    search: name.trim(),
                    page: 0,
                    quantity: 20,
                })
                if (res.data.status === "ok" && res.data.response?.length > 0) {
                    res.data.response.forEach((v: Vehiculo) => {
                        const key = v.vin || v.matricula || v.idInterno
                        if (key) map.set(key, v)
                    })
                }
            }

            setRecords(Array.from(map.values()))
        } catch (error) {
            console.error("Error fetching vehicle records:", error)
        }
    }

    useEffect(() => {
        if (id || name || matricula) bindRecords()
    }, [id, name, matricula])

    if (records.length === 0) return null

    const isMatchQuery = (v: Vehiculo) => {
        if (!matricula) return false
        const clean = matricula.trim().toUpperCase()
        return (
            (v.matricula && v.matricula.toUpperCase() === clean) ||
            (v.vin && v.vin.toUpperCase() === clean) ||
            (v.idInterno && v.idInterno.toUpperCase() === clean)
        )
    }

    return (
        <Card className="my-4">
            <Card.Header className="display-4">
                <i className="fas fa-car me-2" />
                {t("Vehículos")}
            </Card.Header>
            <Card.Body className="p-3">
                <div style={{ overflowX: "auto" }}>
                    <Table size="sm" bordered hover className="small mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>{t("Tipo")}</th>
                                <th>{t("Marca / Modelo")}</th>
                                <th>{t("Año")}</th>
                                <th>{t("Color")}</th>
                                <th>{t("Matrícula")}</th>
                                <th>{t("VIN")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((v, i) => {
                                const isQueried = isMatchQuery(v)
                                return (
                                    <tr key={i} className={isQueried ? "table-primary" : ""}>
                                        <td>{v.tipoVehiculo}</td>
                                        <td className="fw-semibold">
                                            {v.marca} {v.modelo}
                                            {isQueried && (
                                                <Badge bg="primary" className="ms-2">
                                                    Consultado
                                                </Badge>
                                            )}
                                        </td>
                                        <td>{v.anio ?? "—"}</td>
                                        <td>{v.color}</td>
                                        <td className="fw-bold">{v.matricula}</td>
                                        <td><small className="text-muted">{v.vin}</small></td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
        </Card>
    )
}

export default RecordVehiculos
